import React, { useCallback, useEffect, useState, useRef } from "react";
import HeaderListItem from "./HeaderListItem";
import PropTypes from "prop-types";
import { useMobileView } from "components/utility/hooks/useMobileView";
import ImageDimensionalLogo from "components/utility/images/ImageDimensionalLogo";
import ImageHamburgerMenuOpen from "components/utility/images/ImageHamburgerMenuOpen";
import ImageHamburgerMenuClosed from "components/utility/images/ImageHamburgerMenuClosed";
import AudienceSortModal from "../audience-sort/AudienceSortModal";
import { audienceSortStepNames } from "../audience-sort/utility/audienceSortStepNames";
import getAudienceTypesForCountryData from "components/utility/api/GetAudienceTypesForCountryData";
import ProfessionalAffirmation from "../audience-sort/ProfessionalAffirmation";
import CountrySelector from "../audience-sort/CountrySelector";
import AudienceSelector from "../audience-sort/AudienceSelector";
import AudienceSelectorNarrow from "../audience-sort/AudienceSelectorNarrow";
import AudienceSelectorMobile from "../audience-sort/AudienceSelectorMobile";
import ProfessionalAffirmationMobile from "../audience-sort/ProfessionalAffirmationMobile";
import CountrySelectorMobile from "../audience-sort/CountrySelectorMobile";
import HeaderSearchContext from "components/header/context/HeaderContentContext";
import CoveoContainer from "components/coveo-headless/CoveoContainer";
import { headerGA4Props } from "./types/public-header-types";
import { getDecodedHtml } from "utils/getDecodedHtml";
import { ImageArrow, ImageSearchMagnifyingGlass } from "components/utility/images";
import { findPageInformationObject } from "components/coveo-headless/coveoUAHelpers";
import { PageType } from "components/coveo-headless/types/coveo-headless-types";
import { PublicSearchBox } from "components/search/public/PublicSearchBox";
import { SCREEN_WIDTHS } from "components/utility/constants/screen_widths";
import { eventMisfired } from "@dfa/ui-platform-react";

const hideSubNavs = () => {
	if (document.querySelector("li.nav-links-main-label.nav-links-main-list.selected")) {
		document
			.querySelector("li.nav-links-main-label.nav-links-main-list.selected")
			.classList.remove("selected");
	}
};

const HeaderContent = ({ data }) => {
	const pageInfomation = findPageInformationObject(window.dataLayer);
	const isSearchPage = pageInfomation.title === PageType.Search;
	const mobileLinkContainerRef = useRef(null);

	const {
		navigationHidden = true,
		navItems: topNavItems = [],
		logIn = {},
		logoLink = "/",
		hideHamburgerMenu = false,
		hideSearchBox = false
	} = data ? data : {};

	const themes = {
		dark: {
			audienceSortTheme: "dark-theme",
			name: "dark",
			hamburgerSvgColor: "#A2AAAD",
			socialIconsColor: "#fff",
			dimensionalLogoTheme: "white"
		},
		white: {
			audienceSortTheme: "light-theme-gray",
			name: "white",
			hamburgerSvgColor: "#6E787F",
			socialIconsColor: "#6E787F",
			dimensionalLogoTheme: "colored"
		}
	};

	const theme = themes[data?.theme || "dark"];
	const isMobile = useMobileView();
	const currentPage = "nav";

	// for coveo search
	const coveoDiv = document.querySelector("[data-view='coveo-search']");
	const coveoPipelineId = coveoDiv ? coveoDiv.dataset.uniqueid : "myDimensional";

	const [mobileNav, setMobileNav] = useState(false);
	const [showSubNav, setShowSubNav] = useState(-1);
	const [initialData, setInitialData] = useState(null);
	const [audienceTypesData, setAudienceTypesData] = useState(null);
	const [audienceSelectionData, setAudienceSelectionData] = useState(null);
	const [countryListData, setCountryListData] = useState(null);
	const [step, setStep] = useState(audienceSortStepNames.notSelecting);
	const [cameFromInitialStep, setCameFromInitialStep] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalJustOpened, setModalJustOpened] = useState(true);
	const [openSearch, setOpenSearch] = useState(false);

	const setUpInitialData = (data) => {
		setInitialData(data);
		setAudienceTypesData(data);
		setStep(audienceSortStepNames.initialStep);
		setIsModalOpen(true);
		setModalJustOpened(true);
	};

	const openModal = async (e) => {
		if (e.type === "keyup" && e.key === "Escape") return;
		const audienceTypesData = await getAudienceTypesForCountryData({
			isPage: true,
			source: "audienceSwitcherModal",
			countryKey: null
		});

		if (audienceTypesData.status === "success") {
			setUpInitialData(audienceTypesData.data);
		}
	};

	const openMobileAudienceFlow = async () => {
		const audienceTypesData = await getAudienceTypesForCountryData({
			isPage: true,
			source: "audienceSwitcherModal",
			countryKey: null
		});
		if (audienceTypesData.status === "success") {
			setUpInitialData(audienceTypesData.data);
		}
	};

	const mobileToggle = (e) => {
		if (!isMobile || (e.type === "keyup" && ["Tab", "Shift"].includes(e.key))) return;
		setStep(audienceSortStepNames.notSelecting);
		setOpenSearch(mobileNav);
		setMobileNav(!mobileNav);
		setShowSubNav(-1);
	};

	const handleSubNavToggle = async (index) => {
		if (showSubNav === index) {
			return setShowSubNav(-1);
		}
		setShowSubNav(index);
	};

	const hideSubNavOnScroll = useCallback(() => {
		setShowSubNav(-1);
		if (!isMobile) {
			hideSubNavs();
		}
	}, [isMobile]);

	const handleClickOutside = useCallback(
		(e) => {
			if (!isMobile) {
				if (!e.target.classList.contains("nav-links-main-label")) {
					hideSubNavs();
					setShowSubNav(-1);
				}
			}
		},
		[isMobile]
	);

	const handleCloseAudience = (event) => {
		if (event && eventMisfired(event)) return;
		setStep(audienceSortStepNames.notSelecting);
		setOpenSearch(false);
		setMobileNav(false);
		setShowSubNav(-1);
		setIsModalOpen(false);
	};

	useEffect(() => {
		const handleDesktopView = () => {
			// mobile to desktop reset
			if (!isMobile) {
				hideSubNavs();
				setMobileNav(false);
				setShowSubNav(-1);
			}
		};
		handleDesktopView();
		window.addEventListener("resize", handleDesktopView);
		window.addEventListener("scroll", hideSubNavOnScroll);
		window.addEventListener("click", handleClickOutside);

		return () => {
			window.removeEventListener("resize", handleDesktopView);
			window.removeEventListener("scroll", hideSubNavOnScroll);
			window.removeEventListener("click", handleClickOutside);
		};
	}, [handleClickOutside, hideSubNavOnScroll, isMobile]);

	useEffect(() => {
		if (mobileNav) {
			document.body.style.overflowY = "hidden";
			// reduce z-index of media contacts mobile if on Newsroom page
			const mediaContacts = document.querySelector(".media-contacts-mobile");
			if (mediaContacts) mediaContacts.classList.add("media-contacts-mobile-nav-open");
		} else {
			document.body.style.removeProperty("overflow-y");
			// reset z-index of media contacts mobile if on Newsroom page
			const mediaContacts = document.querySelector(".media-contacts-mobile");
			if (mediaContacts) mediaContacts.classList.remove("media-contacts-mobile-nav-open");
		}
	}, [mobileNav]);

	useEffect(() => {
		if (step === audienceSortStepNames.initialStep || step === audienceSortStepNames.notSelecting) {
			setCameFromInitialStep(true);
		} else if (step !== audienceSortStepNames.financialProfessionalAffirmation) {
			setCameFromInitialStep(false);
		}
	}, [step]);

	useEffect(() => {
		const handleResize = () => {
			const mobileLinkContainer = mobileLinkContainerRef.current;
			if (!mobileLinkContainer) return;

			if (mobileNav) {
				const stickyHeaderContainer = document.querySelector(
					".sticky-header-el:has(.global-header)"
				);
				const globalHeaderContainer = document.querySelector(".global-header");
				if (stickyHeaderContainer && globalHeaderContainer) {
					const bannerHeight =
						stickyHeaderContainer.getBoundingClientRect().height -
						globalHeaderContainer.getBoundingClientRect().height +
						"px";
					mobileLinkContainer.style.height = `calc(100% - ${bannerHeight})`;
				}
			} else {
				mobileLinkContainer.style.height = "unset";
			}
		};
		handleResize();

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	});

	const audienceSort = (
		<>
			{step === audienceSortStepNames.initialStep && (
				<AudienceSelectorNarrow
					currentPage={currentPage}
					audienceSortTheme={theme.audienceSortTheme}
					isMobile={isMobile}
					audienceTypesData={initialData}
					countryListData={countryListData}
					setCountryListData={setCountryListData}
					setStep={setStep}
					setAudienceSelectionData={setAudienceSelectionData}
					cameFromInitialStep={cameFromInitialStep}
					setIsModalOpen={setIsModalOpen}
					setModalJustOpened={setModalJustOpened}
				/>
			)}
			{step === audienceSortStepNames.financialProfessionalAffirmation && (
				<ProfessionalAffirmation
					hasCloseButton={false}
					audienceSortTheme={theme.audienceSortTheme}
					audienceSelectionData={audienceSelectionData}
					setStep={setStep}
					cameFromInitialStep={cameFromInitialStep}
					setIsModalOpen={setIsModalOpen}
					currentPage={currentPage}
					setModalJustOpened={setModalJustOpened}
				/>
			)}
			{step === audienceSortStepNames.countrySelection && (
				<CountrySelector
					currentPage={currentPage}
					setStep={setStep}
					hasCloseButton={true}
					isMobile={isMobile}
					countryListData={countryListData}
					setAudienceTypesData={setAudienceTypesData}
					setIsModalOpen={setIsModalOpen}
					setModalJustOpened={setModalJustOpened}
					audienceSortTheme={theme.audienceSortTheme}
				/>
			)}
			{audienceTypesData && step === audienceSortStepNames.audienceTypeSelection && (
				<AudienceSelector
					currentPage={currentPage}
					audienceSortTheme={theme.audienceSortTheme}
					isMobile={isMobile}
					audienceTypesData={audienceTypesData}
					countryListData={countryListData}
					setStep={setStep}
					setAudienceSelectionData={setAudienceSelectionData}
					cameFromInitialStep={cameFromInitialStep}
					setIsModalOpen={setIsModalOpen}
				/>
			)}
		</>
	);

	const audienceSortMobile = (
		<AudienceSortModal
			theme={theme.audienceSortTheme}
			currentPage={currentPage}
			currentStep={step}
			setStep={setStep}
			isMobile={isMobile}
			setIsModalOpen={setIsModalOpen}
			isModalOpen={isModalOpen}
			isWide={true}
			modalJustOpened={false}
			className="audience-sort-modal"
			additionalCloseLogic={handleCloseAudience}
		>
			{step === audienceSortStepNames.initialStep && (
				<AudienceSelectorMobile
					currentPage={currentPage}
					audienceSortTheme={theme.audienceSortTheme}
					isMobile={isMobile}
					audienceTypesData={initialData}
					countryListData={countryListData}
					setCountryListData={setCountryListData}
					setStep={setStep}
					setAudienceSelectionData={setAudienceSelectionData}
					cameFromInitialStep={cameFromInitialStep}
					setIsModalOpen={setIsModalOpen}
					setModalJustOpened={setModalJustOpened}
					customCloseHandler={handleCloseAudience}
					hasCloseButton={true}
				/>
			)}
			{step === audienceSortStepNames.financialProfessionalAffirmation ? (
				window.innerWidth > SCREEN_WIDTHS.XS ? (
					<ProfessionalAffirmation
						hasCloseButton={false}
						audienceSortTheme={theme.audienceSortTheme}
						audienceSelectionData={audienceSelectionData}
						setStep={setStep}
						cameFromInitialStep={cameFromInitialStep}
						setIsModalOpen={setIsModalOpen}
						currentPage={currentPage}
					/>
				) : (
					<ProfessionalAffirmationMobile
						isMobile={isMobile}
						audienceSortTheme={theme.audienceSortTheme}
						audienceSelectionData={audienceSelectionData}
						setStep={setStep}
						cameFromInitialStep={cameFromInitialStep}
						setIsModalOpen={setIsModalOpen}
						currentPage={currentPage}
						setModalJustOpened={setModalJustOpened}
						hasHeader={true}
					/>
				)
			) : null}
			{step === audienceSortStepNames.countrySelection && (
				<CountrySelectorMobile
					currentPage={currentPage}
					setStep={setStep}
					isMobile={isMobile}
					countryListData={countryListData}
					hasCloseButton={true}
					setAudienceTypesData={setAudienceTypesData}
					setIsModalOpen={setIsModalOpen}
					setModalJustOpened={setModalJustOpened}
					audienceSortTheme={theme.audienceSortTheme}
					customCloseHandler={handleCloseAudience}
				/>
			)}
			{audienceTypesData && step === audienceSortStepNames.audienceTypeSelection && (
				<AudienceSelectorMobile
					currentPage={currentPage}
					audienceSortTheme={theme.audienceSortTheme}
					isMobile={isMobile}
					audienceTypesData={audienceTypesData}
					countryListData={countryListData}
					setStep={setStep}
					setAudienceSelectionData={setAudienceSelectionData}
					cameFromInitialStep={cameFromInitialStep}
					setIsModalOpen={setIsModalOpen}
					customCloseHandler={handleCloseAudience}
					hasCloseButton={true}
				/>
			)}
		</AudienceSortModal>
	);

	const SearchButton = (
		<button
			className={`header-public-site-search-button ${openSearch ? "active" : ""}`}
			tabIndex={0}
			data-a-evt="Navigation"
			data-a-text="Search"
			data-a-loc="Header"
			data-a-comp="Global Nav"
			data-qa="public-search-button"
			onClick={() => {
				if (mobileNav) setMobileNav(false);
				setOpenSearch((openSearch) => !openSearch);
			}}
		>
			<ImageSearchMagnifyingGlass />
		</button>
	);

	return navigationHidden ? null : (
		<nav
			className={`global-header trackable public-site ${mobileNav ? "show-mobile-menu" : ""} ${
				theme.name
			}`}
			id="global-header"
			data-component-name="Header"
		>
			{initialData && isModalOpen && !isMobile && (
				<AudienceSortModal
					theme={theme.audienceSortTheme}
					currentPage={currentPage}
					currentStep={step}
					setStep={setStep}
					isMobile={isMobile}
					setIsModalOpen={setIsModalOpen}
					isModalOpen={isModalOpen}
					isWide={step !== audienceSortStepNames.initialStep}
					modalJustOpened={modalJustOpened}
					className="audience-sort-modal"
				>
					{audienceSort}
				</AudienceSortModal>
			)}
			{!isMobile && (
				<div className="container header-cta-section">
					<div className="audience-country-cta">
						<button
							className="audience-country-cta-label"
							aria-label="Audience Switcher"
							tabIndex="0"
							data-a="true"
							data-a-lbl="Audience Switcher"
							data-a-cat="Navigation"
							data-a-act="Header"
							data-qa="nav-header-link-audience-switcher-cta"
							onClick={(event) => (initialData ? setUpInitialData(initialData) : openModal(event))}
							style={{ visibility: data.role && data.country ? "visible" : "hidden" }}
						>
							{data.role ? `${data.role} | ${data.country}` : "Audience Switcher"}
						</button>
					</div>
					{Object.keys(logIn).length > 0 && (
						<div className="nav-login">
							<a
								href={logIn.url}
								target="_self"
								title="Dimensional Client Log In"
								className="nav-login"
								aria-label="Client Log In"
								data-a="true"
								data-a-lbl={logIn.label}
								data-a-cat="Navigation"
								data-a-act="Header"
								data-a-evt={headerGA4Props["data-a-evt"]}
								data-a-loc={headerGA4Props["data-a-loc"]}
								data-a-text={getDecodedHtml(logIn.label)}
								data-a-comp={headerGA4Props["data-a-comp"]}
								data-target-title="Client Log In"
								data-qa="nav-header-link-log-in"
								rel={logIn.isExternalLink ? "noreferrer noopener" : "noopener"}
								role="menuitem"
								dangerouslySetInnerHTML={{ __html: getDecodedHtml(logIn.label) }}
							/>
						</div>
					)}
				</div>
			)}
			<div
				className={`body-mask-placeholder ${mobileNav ? "body-mask-header" : ""}`}
				onClick={mobileToggle}
				role="button"
				tabIndex="0"
				aria-label="mask"
				onKeyUp={mobileToggle}
			></div>
			<div className={`container container-mobile ${mobileNav ? "container-mobile-show" : ""}`}>
				{hideHamburgerMenu ? null : (
					<button
						id="nav-mobile-menu"
						className="nav-mobile-menu"
						onClick={mobileToggle}
						onKeyUp={mobileToggle}
						aria-label="menu"
						tabIndex="0"
					>
						<div className="hamburger-menu">
							<ImageHamburgerMenuOpen fillColor={theme.hamburgerSvgColor} />
						</div>
						<div className="close-menu">
							<ImageHamburgerMenuClosed fillColor={theme.hamburgerSvgColor} />
						</div>
					</button>
				)}
				<a
					href={logoLink}
					target={data.isExternalLink ? "_blank" : "_self"}
					className="logo"
					data-target-title="Dimensional Investing | Dimensional Fund Advisors"
					data-a="true"
					data-a-cat="Navigation"
					data-a-act="Header"
					data-a-comp="Global Nav"
					data-a-evt="Navigation"
					data-a-name="Home Page"
					data-qa="nav-header-link-logo-home"
					aria-label="Dimensional Investing | Dimensional Fund Advisors"
					rel="noopener noreferrer"
				>
					<ImageDimensionalLogo themeChoice={theme.dimensionalLogoTheme} />
				</a>

				{topNavItems.length > 0 ? (
					<div className="nav-links">
						<ul
							className={`nav-links-main ${isMobile ? "nav-links-main-mobile" : ""} ${
								step === audienceSortStepNames.notSelecting ? "main-nav" : ""
							}`}
							ref={mobileLinkContainerRef}
							role="menubar"
						>
							{topNavItems.map((navItem, index) => {
								return !isMobile || step === audienceSortStepNames.notSelecting ? (
									<HeaderListItem
										key={navItem.label}
										dataset={navItem}
										index={index}
										isLastSubNav={index === topNavItems.length - 1}
										mobileNav={mobileNav}
										showSubNav={showSubNav}
										theme={theme}
										onToggle={() => handleSubNavToggle(index)}
									/>
								) : null;
							})}

							{!isMobile && (!hideSearchBox || isSearchPage) && (
								<li className="header-public-site-search">{SearchButton}</li>
							)}

							{step === audienceSortStepNames.notSelecting && isMobile && (
								<>
									{Object.keys(logIn).length && step === audienceSortStepNames.notSelecting ? (
										<HeaderListItem
											key="login"
											className="login-search"
											dataset={{
												url: logIn.url,
												label: logIn.label,
												linkClasses: "nav-login",
												showSocialLinks: false,
												isExternalLink: logIn.isExternalLink,
												ariaLabel: "Client Log In",
												rel: logIn.isExternalLink ? "noreferrer noopener" : "noopener",
												title: "Dimensional Client Log In",
												dataTargetTitle: "Client Log In"
											}}
											index="none" // ensures this will get "show" and "hide" classes from handleSubNavToggle
											isLastSubNav={false}
											mobileNav={mobileNav}
											showSubNav={showSubNav}
											onToggle={() => handleSubNavToggle(topNavItems.length)}
											isLoginLink={logIn.url ? true : false}
										/>
									) : null}
									<li className=" nav-links-main-list audience-country-cta-label nav-links-main-audience  ">
										<div
											onClick={openMobileAudienceFlow}
											onKeyUp={openMobileAudienceFlow}
											role="button"
											tabIndex="0"
											className="audience-country-cta"
											data-qa="audience-country-cta-mobile"
										>
											<span>
												{data.role}
												<br />
												{data.country}
											</span>
											<ImageArrow />
										</div>
									</li>
								</>
							)}
							{isMobile && audienceTypesData && audienceSortMobile}
						</ul>
					</div>
				) : null}
				<HeaderSearchContext.Provider value={{ openSearch, setOpenSearch }}>
					{process.env.NODE_ENV === "development" ? (
						<PublicSearchBox controller={{}} />
					) : (
						<CoveoContainer dataset={{ uniqueid: coveoPipelineId }} />
					)}
				</HeaderSearchContext.Provider>
				{(!hideSearchBox || isSearchPage) && (
					<div className="header-public-site-search mobile">{SearchButton}</div>
				)}
			</div>
		</nav>
	);
};

HeaderContent.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string
};

export default HeaderContent;
